import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import ReactSelect from "react-select";
import { getForms } from "actions/forms";
import { translate } from "lib";

// react-select can't infer its generic from grouped options (it picks the group
// type as the option type), so we use it untyped here — matching how the shared
// Input component consumes react-select.
const Select: any = ReactSelect;

interface Props {
	value?: string;
	onChange: (formId: string | null, form?: any) => void;
	propertyId?: string;
	category?: string;
	placeholder?: string;
}

export interface FormOption {
	value: string;
	label: string;
	form: any;
}

export interface FormOptionGroup {
	label: string;
	options: FormOption[];
}

// The label shown for a form (clean name, falling back to display_code/code).
export const formOptionLabel = (f: any): string => f.name || f.display_code || f.code || "";

// Ascending, numeric-aware so "…-9" sorts before "…-10" (not lexicographically after).
const byFilename = (a: FormOption, b: FormOption) =>
	a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" });

// The redux `forms` store is GLOBAL and cumulative — `GET_FORMS_SUCCESS` unions
// into it rather than replacing it, and single-form fetches land there too. So
// the picker must re-apply the scope itself instead of trusting the store:
//
//   - `parent_form_id` → a per-condition SNAPSHOT (Forms::CreateSnapshot), which
//     clones the template's NAME. `FormFill` loads one via getForm(condition.form_id)
//     — after visiting a filled form, the snapshot sat in the store and the picker
//     listed it beside its template as an identical-looking duplicate row.
//   - `property_id` → forms cached from another property's fetch.
//
// Mirrors the defensive filter on the property Forms tab (PropertyFormsTab).
// Contract: api-contract.md §5 (index returns Form.live.templates only).
const isSelectableTemplate = (f: any, propertyId?: string): boolean => {
	if (!f || f.deleted_at) return false;
	if (f.parent_form_id) return false;
	if (!propertyId) return true;
	const formPropertyId = f.property_id || _.get(f, "property.id");
	// Keep a form whose property is simply absent from the payload rather than
	// dropping it — only a MISMATCH is disqualifying.
	return !formPropertyId || formPropertyId === propertyId;
};

/**
 * Build react-select grouped options from forms: one group (property) each,
 * groups in first-seen order, options sorted alphabetically by filename.
 * The full form is attached as `form` so onChange can hand it back to callers.
 */
export const buildGroupedFormOptions = (forms: any[], noPropertyLabel: string): FormOptionGroup[] => {
	const grouped = (forms || [])
		.filter((f) => isSelectableTemplate(f))
		.reduce((map: Map<string, any[]>, f) => {
			const label = f.property_name || _.get(f, "property.name") || noPropertyLabel;
			if (!map.has(label)) map.set(label, []);
			(map.get(label) as any[]).push(f);
			return map;
		}, new Map<string, any[]>());

	return Array.from(grouped.entries()).map(([label, groupForms]) => ({
		label,
		options: groupForms.map((f) => ({ value: f.id, label: formOptionLabel(f), form: f })).sort(byFilename),
	}));
};

/**
 * Build a FLAT react-select option list (no group headers). Used when the
 * picker is scoped to a single property (`propertyId` set) — grouping by
 * property is meaningless when every form already belongs to the same one
 * (api-contract §2.3: grouping is a frontend-only rendering concern).
 */
export const buildFlatFormOptions = (forms: any[], propertyId?: string): FormOption[] =>
	(forms || [])
		.filter((f) => isSelectableTemplate(f, propertyId))
		.map((f) => ({ value: f.id, label: formOptionLabel(f), form: f }))
		.sort(byFilename);

export default ({ value, onChange, propertyId, category, placeholder }: Props) => {
	const dispatch = useDispatch();
	const forms = useSelector((state: any) => state.forms as any[]);

	useEffect(() => {
		// Only fetch when scoped to a property. A template with no property has
		// no form library to draw from, so we must NOT pull every form the user
		// can see — skip the request entirely (api-contract §2.2).
		// perPage: -1 → fetch every (in-scope) form so the picker never drops one
		// to the default page size.
		if (!propertyId) return;
		dispatch(getForms({ propertyId, category, perPage: -1 }) as any);
	}, [dispatch, propertyId, category]);

	// Scoped to one property → flat list, no group headers. With no property the
	// picker shows nothing at all (and never leaks the shared redux form store),
	// mirroring the fact that no fetch was made.
	const options: FormOption[] | FormOptionGroup[] = propertyId ? buildFlatFormOptions(forms, propertyId) : [];

	// Options can be either a flat FormOption[] or a grouped FormOptionGroup[];
	// flatten defensively so the selected value resolves in both shapes.
	const flatOptions: FormOption[] = _.flatMap(options as any[], (o: any) => (o.options ? o.options : [o]));
	const selectedOption = value ? flatOptions.find((o) => o.value === value) || null : null;

	// Match the app's styled dropdowns (e.g. the "Kind" select in this same
	// modal), which render via react-select with a portalled menu.
	return (
		<Select
			className="react-select"
			classNamePrefix="react-select"
			placeholder={placeholder || translate("select_form")}
			isClearable
			value={selectedOption}
			options={options}
			menuPortalTarget={typeof document !== "undefined" ? document.body : null}
			menuPosition="fixed"
			styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
			onChange={(option: any) => onChange(option ? option.value : null, option ? option.form : undefined)}
		/>
	);
};
